import { DocumentServicesObject, CompRef } from '@wix/document-services-types';
import type { EditorPointer, EditorPointerAPI } from '@wix/editorx-core-api';
import { ManifestServices } from '@wix/editor-platform-manifest-services';
import type { EditorBehaviors } from '@wix/component-protocol/schema';

import { createManifestLoadListeners } from './listeners';
import {
  isBuilderComponentType,
  isTopLevelElement,
  getPresets,
  getChildren,
  getAncestors,
  getCompTypeFromEditorPointer,
  loadAndProcessManifests,
} from '../../utils/builderServices';
import { ManifestServicesAPI } from './types';
import { PanelsStore } from './panelsStore';

export function initManifestServices(
  documentServicesAPI: DocumentServicesObject,
  editorPointerAPI: EditorPointerAPI,
  manifestServices: ManifestServices,
  panelsStore: PanelsStore,
): ManifestServicesAPI {
  const { handleCustomAppEvents, handleNavigateToPage } =
    createManifestLoadListeners(
      documentServicesAPI,
      editorPointerAPI,
      manifestServices,
      panelsStore,
    );

  loadAndProcessManifests({
    documentServicesAPI,
    editorPointerAPI,
    manifestServices,
    panelsStore,
  });

  documentServicesAPI.registerToSiteChanged(handleNavigateToPage);

  documentServicesAPI.platform.subscribeOnCustomAppEvents(
    handleCustomAppEvents,
  );

  const getElementManifest = (editorPointer: EditorPointer<CompRef>) =>
    manifestServices.getElementManifest({
      compType: getCompTypeFromEditorPointer(
        editorPointer,
        editorPointerAPI,
        documentServicesAPI,
      ),
      innerElementPath: editorPointerAPI.getInnerElementPath(editorPointer),
    });

  return {
    behaviors: {
      getValueFromManifest: (
        editorPointer: EditorPointer<CompRef>,
        behaviorName: keyof EditorBehaviors,
      ) => {
        // This is a temporary solution to handle top level elements until the manifest does not have the default behaviors
        // TODO: remove this once the manifest has the default behaviors for top level elements
        if (
          isTopLevelElement(editorPointer, editorPointerAPI) &&
          (behaviorName === 'selectable' || behaviorName === 'removable')
        ) {
          return true;
        }

        const manifest = getElementManifest(editorPointer);

        if (!manifest || !manifestServices.isInnerElement(manifest)) {
          return null;
        }
        return manifest.behaviors?.[behaviorName];
      },
    },
    get: {
      editActions: (editorPointer: EditorPointer<CompRef>) =>
        getElementManifest(editorPointer)?.actions ?? null,
      customActions: (editorPointer: EditorPointer<CompRef>) =>
        getElementManifest(editorPointer)?.customActions ?? null,
      style: (editorPointer: EditorPointer<CompRef>) =>
        getElementManifest(editorPointer)?.style ?? {},
      data: (editorPointer: EditorPointer<CompRef>) =>
        getElementManifest(editorPointer)?.data ?? null,
      presets: (editorPointer: EditorPointer<CompRef>) =>
        getPresets(getElementManifest(editorPointer)),
      displayName: (editorPointer: EditorPointer<CompRef>) =>
        getElementManifest(editorPointer)?.displayName ?? null,
    },
    internal: {
      loadAndResolveManifestsByCompTypes:
        manifestServices.loadAndResolveManifestsByCompTypes.bind(
          manifestServices,
        ),
      getCompType: (editorPointer: EditorPointer<CompRef>) =>
        getCompTypeFromEditorPointer(
          editorPointer,
          editorPointerAPI,
          documentServicesAPI,
        ),
      getPanel: (editorPointer: EditorPointer<CompRef>, panelType: string) => {
        return panelsStore.getPanel(editorPointer, panelType);
      },
    },
    isBuilderComponentType,
    getChildren: (
      editorPointer: EditorPointer<CompRef>,
      isRecursive: boolean,
    ) =>
      getChildren(
        editorPointerAPI,
        editorPointer,
        getElementManifest(editorPointer),
        isRecursive,
      ),
    getAncestors: (editorPointer: EditorPointer<CompRef>) =>
      getAncestors(editorPointerAPI, editorPointer),
  };
}
